import { useEffect, useRef } from 'react';
import { useUIStore } from '@/state/store';

let _ctx: AudioContext | null = null;
function ensureCtx() {
  if (!_ctx) {
    const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    _ctx = new AC();
  }
  return _ctx;
}

export type SoundKind = 'accept' | 'hangup' | 'snap';

export function playSound(kind: SoundKind, prefs: { accept: boolean; hangup: boolean; snap: boolean }) {
  if (!prefs[kind]) return;
  const ctx = ensureCtx();
  const t = ctx.currentTime;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g);
  g.connect(ctx.destination);

  if (kind === 'accept') {
    o.frequency.setValueAtTime(660, t);
    o.frequency.exponentialRampToValueAtTime(880, t + 0.08);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.07, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    o.start(t);
    o.stop(t + 0.2);
  } else if (kind === 'hangup') {
    o.frequency.setValueAtTime(700, t);
    o.frequency.exponentialRampToValueAtTime(440, t + 0.1);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.06, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
    o.start(t);
    o.stop(t + 0.18);
  } else if (kind === 'snap') {
    o.type = 'square';
    o.frequency.setValueAtTime(2200, t);
    o.frequency.exponentialRampToValueAtTime(1100, t + 0.04);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.04, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
    o.start(t);
    o.stop(t + 0.08);
  }
}

// pre175: kleine Helfer zum Synthetisieren von Klingelton-Bausteinen.
function tone(ctx: AudioContext, t0: number, freq: number, dur: number, opts?: { type?: OscillatorType; peak?: number; attack?: number }) {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g);
  g.connect(ctx.destination);
  o.type = opts?.type ?? 'sine';
  o.frequency.value = freq;
  const peak = opts?.peak ?? 0.08;
  const atk = opts?.attack ?? 0.02;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + atk);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

/**
 * pre175: Auswählbare Klingeltöne. Jeder Ton wird synthetisiert (kein Audio-File,
 * offline-tauglich) und über play() auf dem geteilten AudioContext ab Zeit t geplant.
 * periodMs steuert die Wiederholrate im eingehenden Ruf.
 */
export interface Ringtone {
  id: string;
  label: string;
  periodMs: number;
  play: (ctx: AudioContext, t: number) => void;
}

export const RINGTONES: Ringtone[] = [
  {
    id: 'office-chime', label: 'Office Chime', periodMs: 2200,
    play: (ctx, t) => { [880, 660].forEach((f, i) => tone(ctx, t + i * 0.18, f, 0.6)); },
  },
  {
    id: 'klassisch', label: 'Klassisch (Telefon)', periodMs: 3000,
    play: (ctx, t) => {
      // zwei Klingel-Bursts (Doppelton 440/480), wie ein klassisches Telefon
      [0, 0.5].forEach((off) => {
        tone(ctx, t + off, 440, 0.4, { peak: 0.05 });
        tone(ctx, t + off, 480, 0.4, { peak: 0.05 });
      });
    },
  },
  {
    id: 'sanft', label: 'Sanft (Marimba)', periodMs: 2600,
    play: (ctx, t) => { [523.25, 659.25, 783.99].forEach((f, i) => tone(ctx, t + i * 0.14, f, 0.45, { type: 'triangle', peak: 0.07 })); },
  },
  {
    id: 'digital', label: 'Digital', periodMs: 2000,
    play: (ctx, t) => {
      tone(ctx, t, 1318.5, 0.12, { type: 'square', peak: 0.045 });
      tone(ctx, t + 0.16, 1567.98, 0.14, { type: 'square', peak: 0.045 });
    },
  },
  {
    id: 'aufsteigend', label: 'Aufsteigend', periodMs: 2200,
    play: (ctx, t) => { [660, 880, 1100].forEach((f, i) => tone(ctx, t + i * 0.1, f, 0.28, { peak: 0.07 })); },
  },
  {
    id: 'glocke', label: 'Glocke', periodMs: 2800,
    play: (ctx, t) => {
      tone(ctx, t, 740, 1.1, { type: 'sine', peak: 0.09, attack: 0.005 });
      tone(ctx, t, 1110, 0.9, { type: 'sine', peak: 0.035, attack: 0.005 }); // Oberton
    },
  },
];

export function getRingtone(id: string | undefined): Ringtone {
  return RINGTONES.find((r) => r.id === id) ?? RINGTONES[0];
}

/** Einmal-Vorschau eines Klingeltons (für die Einstellungen). */
export function playRingtonePreview(id: string) {
  const ctx = ensureCtx();
  if (ctx.state === 'suspended') void ctx.resume();
  getRingtone(id).play(ctx, ctx.currentTime + 0.02);
}

/** Klingelton bei eingehendem Call — verwendet den in den Einstellungen gewählten Ton. */
export function useRingtone() {
  const view = useUIStore((s) => s.view);
  const ringEnabled = useUIStore((s) => s.audioPrefs.ringtone);
  const ringtoneId = useUIStore((s) => s.audioPrefs.ringtoneId);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    function chime() {
      const prefs = useUIStore.getState().audioPrefs;
      if (!prefs.ringtone) return;
      const ctx = ensureCtx();
      getRingtone(prefs.ringtoneId).play(ctx, ctx.currentTime);
    }

    function stop() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    if (view === 'incoming' && ringEnabled) {
      const period = getRingtone(ringtoneId).periodMs;
      const startTimer = setTimeout(() => {
        chime();
        intervalRef.current = setInterval(chime, period);
      }, 250);
      return () => {
        clearTimeout(startTimer);
        stop();
      };
    }

    stop();
    return undefined;
  }, [view, ringEnabled, ringtoneId]);
}
